/* eslint-disable tailwindcss/no-arbitrary-value */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@one-thd/sui-atomic-components';
import { useConfigService } from '@thd-nucleus/experience-context';
import { formatDistance } from '../helpers/pod-fulfillment-utils';
import { FulfillmentPodContext } from '../FulfillmentPod';

export const PodMessage = ({
  messageIcon,
  messageType = 'Pickup',
  isStoreNameAtEnd = false,
  children = null,
  isNewFulfillmentPodMessagingEnabled = false,
  nearbyStore = null,
  distance = 0,
  isNarrowPod = false
}) => {
  const enablePLPFasterDeliveryOption = !!useConfigService('fs:enablePLPFasterDeliveryOption');
  // A selectable pod that is showing a radio in place of the icon sets hideIcon on the
  // FulfillmentPodContext; defaults to shown (and stays shown for unavailable/disabled outcomes).
  const { hideIcon = false } = useContext(FulfillmentPodContext) || {};

  const displayMilesForPickup = () => (
    (isNewFulfillmentPodMessagingEnabled && nearbyStore && distance)
    && (
      <span className="sui-text-primary">{` (${formatDistance(distance)} mi)`}
      </span>
    )
  );

  const getLineClampNumber = (message) => {
    switch (message) {
    case 'Pickup Nearby':
    case 'At Your Store':
      return isNarrowPod ? 3 : 2;
    case 'Limited Stock Nearby':
      return 2;
    case 'In-Store Clearance':
      return isNarrowPod ? 5 : 3;
    case 'Pickup':
      return isNarrowPod ? 2 : 'none';
    case 'Ship to Store':
      return isNarrowPod ? 2 : 'none';
    case 'Limited Stock':
      return isNarrowPod ? 2 : 'none';
    default:
      return 'none';
    }
  };

  if (enablePLPFasterDeliveryOption) {
    const lineClamp = getLineClampNumber(messageType);
    return (
      <div
        data-testid="pod-message"
        data-component={`podMessage${messageType}`}
        className={`sui-flex ${messageType === 'Delivery' || messageType === 'Faster Delivery' ? 'sui-mt-[2px]' : ''}`}
      >
        {!hideIcon && !isNarrowPod && <div className="sui-pr-2 sui-mt-[6px]">{messageIcon}</div>}
        <Typography variant="body-sm" lineClamp={lineClamp} component="div">
          <div className="sui-pt-1">
            <span className="sui-font-bold">{`${messageType}: `}</span>
            <span className={`sui-text-sm ${isStoreNameAtEnd && lineClamp === 1 ? 'sui-break-all' : ''}`}>
              {children}
            </span>
          </div>
        </Typography>
      </div>
    );
  }

  return (
    <div
      data-testid="pod-message"
      data-component={`podMessage${messageType}`}
      className={`sui-flex ${messageType === 'Delivery' ? 'sui-mt-2' : ''}`}
    >
      {!hideIcon && !isNarrowPod && <div data-testid="pod-message-icon" className="sui-mt-2 sui-pr-2">{messageIcon}</div>}
      <Typography variant="body-sm" component="div">
        <div className="sui-pt-1">
          <span className="sui-font-bold">{messageType}</span>
          {displayMilesForPickup()}
          <div className={`sui-text-sm ${isStoreNameAtEnd ? 'sui-break-all' : ''}`}>
            {children}
          </div>
        </div>
      </Typography>
    </div>
  );
};

PodMessage.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  messageIcon: PropTypes.any.isRequired,
  messageType: PropTypes.string,
  isStoreNameAtEnd: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]),
  isNewFulfillmentPodMessagingEnabled: PropTypes.bool,
  nearbyStore: PropTypes.shape({
    storeName: PropTypes.string,
    distance: PropTypes.number,
  }),
  distance: PropTypes.number,
  isNarrowPod: PropTypes.bool
};
